import { ref } from 'vue'
import {
  createDevLabOriginId,
  DEV_LAB_TOAST_CHANNEL,
  type DevLabToastMessage,
} from '~~/utils/devLabCrossFrame'

export type SourceToastVariant = 'success' | 'error' | 'warning' | 'info'

export interface SourceToastEntry {
  id: string
  variant: SourceToastVariant
  title: string
  description: string
  durationMs: number
  open: boolean
}

export const DEFAULT_SOURCE_TOAST_DURATION_MS = 5000

/** Délai avant retrait du DOM après fermeture (animation sortie Presence / CSS). */
const SOURCE_TOAST_REMOVE_AFTER_MS = 280

/** Marge commune au bas de l’écran (toolbox + ancrage pile de toasts). */
export const APP_CORNER_BOTTOM_MARGIN_PX = 20

/** Espace entre le haut de la colonne toolbox et le bas de la pile de toasts. */
export const GAP_TOAST_STACK_ABOVE_TOOLBOX_PX = 12

/**
 * Hauteur minimale d’une carte toast (alignée sur `SourceToastItem`, barre + contenu).
 * Les cartes peuvent dépasser cette valeur ; `sourceToastStackUnitPx` sert d’approximation pour les indices de scroll.
 */
const SOURCE_TOAST_CARD_MIN_HEIGHT_PX = 96

/** Espacement vertical entre deux toasts dans la pile. */
const SOURCE_TOAST_STACK_GAP_PX = 12

export function sourceToastStackUnitPx(): number {
  return SOURCE_TOAST_CARD_MIN_HEIGHT_PX + SOURCE_TOAST_STACK_GAP_PX
}

const toasts = ref<SourceToastEntry[]>([])

const defaultDurationMs = ref(DEFAULT_SOURCE_TOAST_DURATION_MS)

/**
 * Hauteur visible (px) du conteneur fixe des toasts (`.source-toast-stack-anchor`).
 */
const toastStackAnchorClientHeightPx = ref(0)

/**
 * Hauteur réservée pour la colonne toolbox (FAB ± rail) : les toasts s’ancrent au-dessus.
 * Mis à jour par `AppToolbox` ; 0 si la toolbox n’est pas montée (ex. paramètres / admin).
 */
const toolboxColumnReservePx = ref(0)

const toastOriginId = createDevLabOriginId()
let toastChannelBound = false

function generateToastId(): string {
  if (import.meta.client && typeof globalThis.crypto?.randomUUID === 'function') {
    return globalThis.crypto.randomUUID()
  }
  return `toast-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`
}

function scheduleRemoveAfterClose(toastId: string): void {
  if (!import.meta.client) {
    return
  }
  globalThis.setTimeout(() => {
    toasts.value = toasts.value.filter((entry) => entry.id !== toastId)
  }, SOURCE_TOAST_REMOVE_AFTER_MS)
}

function ensureDevToastChannel(): void {
  if (!import.meta.client || !import.meta.dev || toastChannelBound) {
    return
  }
  if (typeof BroadcastChannel === 'undefined') {
    return
  }
  toastChannelBound = true
  const channel = new BroadcastChannel(DEV_LAB_TOAST_CHANNEL)
  channel.addEventListener('message', (event: MessageEvent<DevLabToastMessage>) => {
    const data = event.data
    if (!data || data.originId === toastOriginId) {
      return
    }
    if (data.type === 'push') {
      pushToastLocal(data.variant, data.title, data.description, data.durationMs)
      return
    }
    if (data.type === 'clear') {
      toasts.value = []
      return
    }
    if (data.type === 'setDefaultDuration' && data.ms > 0) {
      defaultDurationMs.value = data.ms
    }
  })
}

function broadcastDevToast(message: DevLabToastMessage): void {
  if (!import.meta.client || !import.meta.dev || typeof BroadcastChannel === 'undefined') {
    return
  }
  ensureDevToastChannel()
  const channel = new BroadcastChannel(DEV_LAB_TOAST_CHANNEL)
  channel.postMessage(message)
  channel.close()
}

function pushToastLocal(
  variant: SourceToastVariant,
  title: string,
  description: string,
  durationMs?: number,
): string {
  const resolvedDuration =
    typeof durationMs === 'number' && durationMs > 0 ? durationMs : defaultDurationMs.value

  const entry: SourceToastEntry = {
    id: generateToastId(),
    variant,
    title,
    description,
    durationMs: resolvedDuration,
    open: true,
  }
  toasts.value = [...toasts.value, entry]
  return entry.id
}

function pushToast(
  variant: SourceToastVariant,
  title: string,
  description: string,
  durationMs?: number,
): string {
  ensureDevToastChannel()
  const id = pushToastLocal(variant, title, description, durationMs)
  broadcastDevToast({
    type: 'push',
    originId: toastOriginId,
    variant,
    title,
    description,
    durationMs,
  })
  return id
}

function onToastOpenChange(entry: SourceToastEntry, open: boolean): void {
  entry.open = open
  if (!open) {
    scheduleRemoveAfterClose(entry.id)
  }
}

function dismiss(toastId: string): void {
  const entry = toasts.value.find((item) => item.id === toastId)
  if (!entry || !entry.open) {
    return
  }
  entry.open = false
  scheduleRemoveAfterClose(toastId)
}

function setDefaultDurationMs(milliseconds: number): void {
  if (milliseconds > 0) {
    defaultDurationMs.value = milliseconds
    broadcastDevToast({ type: 'setDefaultDuration', originId: toastOriginId, ms: milliseconds })
  }
}

function clearAllToasts(): void {
  toasts.value = []
  broadcastDevToast({ type: 'clear', originId: toastOriginId })
}

function setToolboxColumnReservePx(pixels: number): void {
  if (pixels < 0) {
    toolboxColumnReservePx.value = 0
    return
  }
  toolboxColumnReservePx.value = pixels
}

export function useSourceToast() {
  ensureDevToastChannel()
  return {
    toasts,
    toastStackAnchorClientHeightPx,
    toolboxColumnReservePx,
    setToolboxColumnReservePx,
    defaultDurationMs,
    setDefaultDurationMs,
    clearAllToasts,
    success(title: string, description: string, durationMs?: number) {
      return pushToast('success', title, description, durationMs)
    },
    error(title: string, description: string, durationMs?: number) {
      return pushToast('error', title, description, durationMs)
    },
    warning(title: string, description: string, durationMs?: number) {
      return pushToast('warning', title, description, durationMs)
    },
    info(title: string, description: string, durationMs?: number) {
      return pushToast('info', title, description, durationMs)
    },
    dismiss,
    onToastOpenChange,
  }
}
